import { getAllowedVehicleList } from "@vahak/core/dist/constants/lorryTypes";

import { IMarketplaceConfig } from "../../MarketplaceContextProvider";
import { getMktContentByCity } from "../marketplace/content/content";

export type MarketplaceFaqItem = { question: string; answer: string };

export type MarketplaceLegacyCityMeta = {
    city: string;
    CITY_TEXT_WITHOUT_INDIA: string;
    IN_CITY_TEXT: string;
    IN_CITY_TEXT_WITHOUT_INDIA: string;
    META_TITLE: string;
    META_DESCRIPTION: string;
    META_KEYWORDS: string;
};

export function getLorryTypeFromParam(lorryParam: string | string[]) {
    const req = Array.isArray(lorryParam) ? lorryParam : [lorryParam];

    return getAllowedVehicleList("lorry").filter((lorryType) => req?.includes(lorryType.label.toLowerCase()));
}

export function botTest(userAgent: string) {
    return /bot|crawler|spider|googlebot|bingbot|msnbot|yandexbot|baiduspider|slurp|duckduckbot/i.test(userAgent);
}

/** Mirrors SSR parsing in `pages/marketplace/[...params].tsx`. */
export function parseMarketplaceLocationFromParamSegment(segment?: string) {
    if (!segment) {
        return { src: "", dest: "" };
    }

    const locationParts = segment.split("to-");

    return {
        src: locationParts[0]?.replace(/-/g, " ") ?? "",
        dest: locationParts.length === 2 ? locationParts[1]?.replace(/-/g, " ") ?? "" : ""
    };
}

/** Format a marketplace URL slug (e.g. bengaluru-karnataka-india) for display in meta copy. */
export function formatMarketplaceLocationSlug(slug: string) {
    return slug
        .replace(/-india$/i, "")
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Format SSR-parsed location text (e.g. "bengaluru karnataka india") for display. */
export function formatMarketplaceLocationLabel(label: string) {
    return label
        .replace(/\s+india\s*$/i, "")
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .trim();
}

/** Legacy-compatible "in Bengaluru Karnataka" text for on-page SEO content lookup. */
export function getMarketplaceInCityTextFromSegment(locationSegment?: string) {
    if (!locationSegment) {
        return "";
    }

    const cityDisplayName = formatMarketplaceLocationSlug(locationSegment);
    return cityDisplayName ? `in ${cityDisplayName}` : "";
}

/** Shared in-city phrase for FAQ blocks, H1 copy, and legacy marketplace pages. */
export function getMarketplaceInCityTextForSeoContent({
    locationSegment,
    urlSrc = "",
    urlDest = ""
}: {
    locationSegment?: string;
    urlSrc?: string;
    urlDest?: string;
}) {
    const seoContext = resolveMarketplaceLoadSeoContext({ locationSegment, urlSrc, urlDest });

    if (seoContext.kind === "city" || seoContext.kind === "destination") {
        return seoContext.inCityText;
    }

    if (locationSegment) {
        return getMarketplaceInCityTextFromSegment(locationSegment);
    }

    return "";
}

export type MarketplaceLoadSeoContext =
    | { kind: "default" }
    | { kind: "city"; cityDisplayName: string; inCityText: string }
    | { kind: "route"; fromCity: string; toCity: string; routeLabel: string }
    | { kind: "destination"; cityDisplayName: string; inCityText: string };

export function resolveMarketplaceLoadSeoContext({
    locationSegment,
    urlSrc = "",
    urlDest = ""
}: {
    locationSegment?: string;
    urlSrc?: string;
    urlDest?: string;
}): MarketplaceLoadSeoContext {
    const parsed = parseMarketplaceLocationFromParamSegment(locationSegment);
    const src = (urlSrc || parsed.src).trim();
    const dest = (urlDest || parsed.dest).trim();

    if (src && dest) {
        const fromCity = formatMarketplaceLocationLabel(src);
        const toCity = formatMarketplaceLocationLabel(dest);

        return {
            kind: "route",
            fromCity,
            toCity,
            routeLabel: `${fromCity} to ${toCity}`
        };
    }

    if (dest) {
        const cityDisplayName = formatMarketplaceLocationLabel(dest);

        return {
            kind: "destination",
            cityDisplayName,
            inCityText: `in ${cityDisplayName}`
        };
    }

    if (src) {
        const cityDisplayName = formatMarketplaceLocationLabel(src);

        return {
            kind: "city",
            cityDisplayName,
            inCityText: `in ${cityDisplayName}`
        };
    }

    if (locationSegment?.startsWith("to-")) {
        const cityDisplayName = formatMarketplaceLocationSlug(locationSegment.replace(/^to-/, ""));

        return {
            kind: "destination",
            cityDisplayName,
            inCityText: `in ${cityDisplayName}`
        };
    }

    if (locationSegment) {
        const cityDisplayName = formatMarketplaceLocationSlug(locationSegment);

        return {
            kind: "city",
            cityDisplayName,
            inCityText: `in ${cityDisplayName}`
        };
    }

    return { kind: "default" };
}

/** City-aware FAQ for marketplace load pages — falls back to generic list when no location in URL. */
export function getMarketplaceLoadFaqList({
    locationSegment,
    urlSrc = "",
    urlDest = "",
    genericFaqList
}: {
    locationSegment?: string;
    urlSrc?: string;
    urlDest?: string;
    genericFaqList: MarketplaceFaqItem[];
}) {
    const inCityText = getMarketplaceInCityTextForSeoContent({ locationSegment, urlSrc, urlDest });

    if (inCityText) {
        return getMktContentByCity(inCityText, true).faqList;
    }

    return genericFaqList;
}

/** English meta copy for legacy marketplace v2 (load + lorry routes). */
export function getMarketplaceLegacyCityMeta({
    locationSegment,
    urlSrc = "",
    urlDest = "",
    isLoadMarket,
    fallbackCityText = ""
}: {
    locationSegment?: string;
    urlSrc?: string;
    urlDest?: string;
    isLoadMarket: boolean;
    fallbackCityText?: string;
}): MarketplaceLegacyCityMeta {
    const seoContext = resolveMarketplaceLoadSeoContext({ locationSegment, urlSrc, urlDest });

    let cityDisplayName = fallbackCityText;
    if (seoContext.kind === "city" || seoContext.kind === "destination") {
        cityDisplayName = seoContext.cityDisplayName;
    } else if (seoContext.kind === "route") {
        cityDisplayName = seoContext.routeLabel;
    } else if (locationSegment) {
        cityDisplayName = formatMarketplaceLocationSlug(locationSegment);
    }

    const cityTextWithoutIndia = cityDisplayName;
    const inCityTextWithoutIndia = cityDisplayName ? `in ${cityDisplayName}` : "";
    const inCityText = inCityTextWithoutIndia;

    const metaTitle = isLoadMarket
        ? cityDisplayName
            ? `Find Truck Load Online in ${cityDisplayName} - India's Top Transport Marketplace`
            : `Find Instant Truck Load Online - India's Top Transport Marketplace`
        : cityDisplayName
        ? `Find Online Truck Booking in ${cityDisplayName} - India's Top Marketplace`
        : `Book Your Truck Online : India's Top Lorry Booking Marketplace`;

    const metaDescription = isLoadMarket
        ? `Book truck loads online in ${cityDisplayName} with ease. Our online platform offers GST and Aadhaar Verified Transport Services, truck owners and agents at your convenience. Book now.`
        : `Find instant truck, load, and lorry bookings online in ${cityDisplayName} on India's largest transport marketplace. Streamline your logistics with ease. Register & Bid Now!`;

    const metaKeywords = isLoadMarket
        ? `truck load booking near me, online load booking ${inCityText}, load booking ${cityDisplayName}, find truck loads ${inCityText}, find load for my truck ${inCityText}, how to find load for truck, truck attachment ${inCityText}`
        : `truck booking ${cityDisplayName}, online truck booking ${inCityText}, truck booking app ${cityDisplayName}, mini truck transport ${inCityText}, online mini truck booking ${cityDisplayName}`;

    return {
        city: cityDisplayName,
        CITY_TEXT_WITHOUT_INDIA: cityTextWithoutIndia,
        IN_CITY_TEXT: inCityText,
        IN_CITY_TEXT_WITHOUT_INDIA: inCityTextWithoutIndia,
        META_TITLE: metaTitle,
        META_DESCRIPTION: metaDescription,
        META_KEYWORDS: metaKeywords
    };
}

export interface MarketPlacePageProps {
    marketplaceType: string;
    urlSrc: string;
    urlDest: string;
    urlFilters: IMarketplaceConfig | null;
    route?: string;
    embeddedWithProfile?: boolean;
    urlPage?: number | null;
    data?: null;
}
